import React from "react";
import { useEffect } from "react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { getContentsByCustomerId, getWorkExperience } from "../../api/contents";
import { getFirstChar, renderMonth } from "../../helpers/common";

const ExperienceJourney = ({
  handleOpenExperienceModal,
  componentType = "overview",
  initialData = null, // 🟢 Prioritize profile payload data safely passed from parent card
}) => {
  const { candidateId } = useParams();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);

  // Month mapping index helper in case renderMonth helper is missing in scope
  const getMonthName = (monthValue) => {
    if (!monthValue) return "";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    // If it's a number (e.g. "1" or 1), return matching month name
    const num = parseInt(monthValue, 10);
    if (!isNaN(num) && num >= 1 && num <= 12) {
      return months[num - 1];
    }
    return monthValue; // fallback if it is already a string like "January" or "Jan"
  };

  useEffect(() => {
    // 🟢 1. Prioritize prop-level initialData array/string block
    if (initialData !== null) {
      let parsedData = [];
      if (typeof initialData === "string") {
        try {
          parsedData = JSON.parse(initialData);
        } catch (e) {
          console.error(
            "Failed to parse initialData string in ExperienceJourney:",
            e,
          );
          parsedData = [];
        }
      } else if (Array.isArray(initialData)) {
        parsedData = initialData;
      }
      setExperiences(parsedData);
      setLoading(false);
      return;
    }

    // 🟢 2. Fallback to API retrieval if no initialData is present
    console.log(
      "🚀 UseEffect Triggered! Fetching personal experiences via context...",
    );
    setLoading(true);

    getWorkExperience()
      .then((res) => {
        const dataPayload = res?.data?.data || res?.data || [];
        const parsedPayload =
          typeof dataPayload === "string"
            ? JSON.parse(dataPayload)
            : dataPayload;
        setExperiences(parsedPayload);
      })
      .catch((err) => {
        console.error("Failed to load user experience profile details:", err);
      })
      .finally(() => setLoading(false));
  }, [initialData]);

  // Reads direct flat fields first, fallbacks to snake_case additionalFields next
  const startData = (item, af) => {
    const month = item?.startMonth || af?.start_month || "";
    const year = item?.startYear || af?.start_year || "";

    const formattedMonth = getMonthName(month);
    return formattedMonth && year
      ? `${formattedMonth} ${year}`
      : year || formattedMonth || "";
  };

  // Reads direct flat fields first, safely identifies current active roles
  const endData = (item, af) => {
    const isWorking =
      item?.isCurrentlyWorking !== undefined
        ? item.isCurrentlyWorking
        : af?.is_currently_working === "Yes" ||
          af?.is_currently_working === true ||
          item?.currentlyWorking === true;

    if (isWorking) {
      return "Present";
    }

    const month = item?.endMonth || af?.end_month || "";
    const year = item?.endYear || af?.end_year || "";

    const formattedMonth = getMonthName(month);
    return formattedMonth && year
      ? `${formattedMonth} ${year}`
      : year || formattedMonth || "";
  };

  const getCompanyAndYear = (item, af) => {
    let companyAndYear = "";
    const company =
      item?.company || item?.companyName || af?.company_name || "";
    const sYear = item?.startYear || af?.start_year || "";

    if (company) {
      companyAndYear += `${company} `;
    }

    if (sYear) {
      companyAndYear += `(${startData(item, af)} - ${endData(item, af)})`;
    }

    return companyAndYear;
  };

  // Sorts recent roles and active "Present" jobs at the very top
  const sortedExperiences = [...experiences].sort((a, b) => {
    const afA = a?.additional_fields || a?.additionalFields || {};
    const afB = b?.additional_fields || b?.additionalFields || {};

    const isWorkingA =
      a?.isCurrentlyWorking !== undefined
        ? a.isCurrentlyWorking
        : afA.is_currently_working === "Yes" ||
          afA.is_currently_working === true ||
          a?.currentlyWorking === true;
    const isWorkingB =
      b?.isCurrentlyWorking !== undefined
        ? b.isCurrentlyWorking
        : afB.is_currently_working === "Yes" ||
          afB.is_currently_working === true ||
          b?.currentlyWorking === true;

    if (isWorkingA && !isWorkingB) return -1;
    if (!isWorkingA && isWorkingB) return 1;

    const yearA = parseInt(a?.startYear || afA?.start_year) || 0;
    const yearB = parseInt(b?.startYear || afB?.start_year) || 0;
    return yearB - yearA;
  });

  const getFirstChar = (str) => {
    if (!str) return "W";
    return str.charAt(0).toUpperCase();
  };

  return (
    <>
      {loading ? (
        <p style={{ margin: "25px 0" }}>Loading...</p>
      ) : sortedExperiences && sortedExperiences.length > 0 ? (
        sortedExperiences.map((item, index) => {
          const additionalFields =
            item?.additional_fields || item?.additionalFields || {};

          const position =
            item?.position ||
            item?.name ||
            item?.jobTitle ||
            additionalFields?.position ||
            "Work Experience";
          const details = item?.details || additionalFields?.details || "";

          return (
            <div
              key={item?.id || index}
              className="candidate-education-content mt-4 d-flex align-items-start"
            >
              <div
                className="circle flex-shrink-0 bg-soft-primary d-flex align-items-center justify-content-center rounded-circle fw-bold text-primary fs-14"
                style={{ width: "40px", height: "40px" }}
              >
                {getFirstChar(position)}
              </div>
              <div className="ms-3 w-100">
                <div className="d-flex align-items-center justify-content-between">
                  <h6 className="fs-16 mb-1 text-dark fw-semibold text-capitalize">
                    {position}
                  </h6>

                  {componentType === "update" ? (
                    <div
                      className="cursor-pointer text-muted px-2"
                      onClick={() => handleOpenExperienceModal("update", item)}
                    >
                      <i className="uil uil-edit"></i>
                    </div>
                  ) : null}
                </div>

                <p className="mb-2 text-muted fs-14 text-capitalize">
                  {getCompanyAndYear(item, additionalFields)}
                </p>
                {details && <p className="text-muted fs-14 mb-0">{details}</p>}
              </div>
            </div>
          );
        })
      ) : (
        <>
          <p className="mt-4 mb-5 text-muted fs-14 fst-italic">
            No work experiences have been added.
          </p>
        </>
      )}
    </>
  );
};

export default ExperienceJourney;
